import { useRef, useState, useCallback } from 'react';
import { Trash2 } from 'lucide-react';

/**
 * SwipeToDelete — wraps a cart row with a touch swipe-left-to-remove gesture.
 * Desktop unaffected (touch events only fire on touch devices). If the user
 * swipes past 40% of the row width, the row is removed on release; otherwise
 * it springs back. A red delete affordance is revealed behind the row as the
 * user drags.
 *
 * Usage:
 *   <SwipeToDelete onDelete={() => removeItem(id)}>
 *     ...row content...
 *   </SwipeToDelete>
 */
export default function SwipeToDelete({ children, onDelete, threshold = 0.4 }) {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const width = useRef(1);
  const wrapperRef = useRef(null);
  const axisLocked = useRef(null); // 'x' | 'y' | null

  const onTouchStart = useCallback((e) => {
    if (!e.touches || e.touches.length !== 1) return;
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    width.current = wrapperRef.current?.offsetWidth || 1;
    axisLocked.current = null;
    setDragging(true);
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!dragging) return;
    const t = e.touches[0];
    const rawDx = t.clientX - startX.current;
    const rawDy = t.clientY - startY.current;
    if (axisLocked.current == null) {
      if (Math.abs(rawDx) < 6 && Math.abs(rawDy) < 6) return;
      axisLocked.current = Math.abs(rawDx) > Math.abs(rawDy) ? 'x' : 'y';
    }
    if (axisLocked.current === 'y') return; // let the page scroll
    // Only allow leftward drag (negative dx)
    setDx(Math.min(0, rawDx));
  }, [dragging]);

  const onTouchEnd = useCallback(() => {
    if (!dragging) return;
    setDragging(false);
    const passed = Math.abs(dx) > width.current * threshold;
    if (passed && axisLocked.current === 'x') {
      // animate out then remove
      setDeleting(true);
      setDx(-width.current);
      setTimeout(() => {
        onDelete && onDelete();
      }, 220);
    } else {
      setDx(0);
    }
  }, [dragging, dx, threshold, onDelete]);

  const percent = Math.min(1, Math.abs(dx) / (width.current * threshold));

  return (
    <div ref={wrapperRef} className="relative overflow-hidden">
      {/* Red delete affordance underneath */}
      <div
        className="absolute inset-0 flex items-center justify-end pr-6 pointer-events-none"
        style={{
          background: `rgba(185, 28, 28, ${Math.max(0.15, percent * 0.9)})`,
        }}
        aria-hidden="true"
      >
        <span className="sans text-white text-xs tracking-[0.28em] uppercase flex items-center gap-2">
          <Trash2 size={16} strokeWidth={1.7} /> Remove
        </span>
      </div>
      <div
        className="relative bg-white"
        style={{
          transform: `translateX(${dx}px)`,
          transition: dragging ? 'none' : (deleting ? 'transform 220ms ease-out' : 'transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1)'),
          touchAction: 'pan-y',
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        {children}
      </div>
    </div>
  );
}
